import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import fp from "lodash/fp"

import type { PowerlineIdByLessId } from "types/powerlines.types"
import type { TowerIdByLessId } from "types/towers.types"
import type {
  Device,
  DevicesByInstallationStatus,
  DeviceSettings,
  IDeviceWRelsById,
  IDevsWRelsByIdWStates,
  ILessIdByDeviceId,
} from "types/device.types"

import {
  isVirtualDevice,
  isVirtualPowerline,
  isVirtualTower,
  getDevicesByInstallationStatus,
} from "helpers/utils/devices"
import { formatDevsWRelsById } from "helpers/formatters/dataFormatters"
import { formatEntitiesWithStates } from "helpers/formatters/alertSettingsFormatters"
import { selectCurrentOrgId } from "features/store/orgSlice"
import { useDevicesQuery } from "features/api"
import { DEVICES_TABLE_TIME } from "helpers/utils/constants"
import useAlertSettingsWStates from "./useAlertSettingsStates"
import useDeviceGroups from "./useDeviceGroups"

interface UseDevicesOptions {
  skip?: boolean
}

interface UseDevicesReturn {
  isSuccess: boolean
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  isUninitialized: boolean
  devsWRelsById: IDeviceWRelsById | undefined
  devsWRelsByIdWStates: IDevsWRelsByIdWStates
  devicesByInstallationStatus: DevicesByInstallationStatus
  lessIdByDeviceId: ILessIdByDeviceId | undefined
  powerlineIdByLessId: PowerlineIdByLessId
  towerIdByLessId: TowerIdByLessId
  getLessIdFromPowerlineId: (powerlineId: number) => number | undefined
  getLessIdFromTowerId: (towerId: number) => number | undefined
}

const useDevices = ({ skip = false }: UseDevicesOptions = {}): UseDevicesReturn => {
  const orgId = useSelector(selectCurrentOrgId)
  const { groups } = useDeviceGroups()
  const { alertSettingsByDeviceId, statesBySettingId } = useAlertSettingsWStates()
  const { currentData, isFetching, isLoading, isError, isSuccess, isUninitialized } =
    useDevicesQuery(
      {
        params: { org_id: orgId as number },
      },
      {
        skip: skip || !orgId,
        pollingInterval: DEVICES_TABLE_TIME,
      },
    )

  const orgDevices = useMemo(
    () => fp.filter((device) => !isVirtualDevice(device.device_type), currentData),
    [currentData],
  )

  const { devsWGroups, lessIdByDeviceId } = useMemo(
    () => formatDevsWRelsById(groups, orgDevices),
    [groups, orgDevices],
  )

  const devsWRelsById = devsWGroups

  const devsWRelsByIdWStates = useMemo(
    () =>
      formatEntitiesWithStates(
        devsWRelsById,
        alertSettingsByDeviceId,
        statesBySettingId,
      ) as IDevsWRelsByIdWStates,
    [devsWRelsById, alertSettingsByDeviceId, statesBySettingId],
  )

  const devicesByInstallationStatus = useMemo(
    () => getDevicesByInstallationStatus(devsWRelsByIdWStates),
    [devsWRelsByIdWStates],
  )

  const powerlineIdByLessId: { [key: number]: number } = useMemo(
    () =>
      fp.flow(
        fp.filter(
          (device: Device) =>
            isVirtualPowerline(device.device_type) && !!device.settings?.powerline_id,
        ),
        fp.map((device: Device) => [
          device.less_id,
          (device.settings as DeviceSettings).powerline_id,
        ]),
        fp.fromPairs,
      )(currentData),
    [currentData],
  )

  const towerIdByLessId: { [key: number]: number } = useMemo(
    () =>
      fp.flow(
        fp.filter(
          (device: Device) =>
            isVirtualTower(device.device_type) && !!device.settings?.tower_id,
        ),
        fp.map((device: Device) => [
          device.less_id,
          (device.settings as DeviceSettings).tower_id,
        ]),
        fp.fromPairs,
      )(currentData),
    [currentData],
  )

  const getLessIdFromPowerlineId = useCallback(
    (powerlineId: number) => {
      const id = fp.findKey((pId) => pId === powerlineId, powerlineIdByLessId)
      return id ? Number(id) : undefined
    },
    [powerlineIdByLessId],
  )

  const getLessIdFromTowerId = useCallback(
    (towerId: number) => {
      const id = fp.findKey((tId) => tId === towerId, towerIdByLessId)
      return id ? Number(id) : undefined
    },
    [towerIdByLessId],
  )

  return {
    devsWRelsById,
    devsWRelsByIdWStates,
    devicesByInstallationStatus,
    lessIdByDeviceId,
    powerlineIdByLessId,
    towerIdByLessId,
    getLessIdFromPowerlineId,
    getLessIdFromTowerId,
    isError,
    isSuccess,
    isFetching,
    isLoading,
    isUninitialized,
  }
}

export default useDevices
